import './styles.scss'
import React, { useState, useRef, useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Divider } from 'primereact/divider'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import ComingSoonPopUp from 'Components/UIComponent/ComingSoonPopUp'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

const CoffeeCast = () => {
  const { t } = useTranslation()
  const playerRef = useRef(null)
  const videoRef = useRef(null)
  const topRef = useRef(null)
  const [isPaused, setIsPaused] = useState(true)
  const [activeBreakpointIndex, setActiveBreakpointIndex] = useState(0)
  const breakpointsRef = useRef([])

  const location = useLocation()
  const userDetails = getLocalUserDetails()
  const { setLoading } = useContext(CoffeeWebContext)
  const [coffeeCastVideos, setCoffeeCastVideos] = useState([])
  const [selectedVideoDetails, setSelectedVideoDetails] = useState([])
  const [isFullText, setIsFullText] = useState(false)
  const [overlay, setOverlay] = useState({ show: false, type: null, text: '' })
  const overlayTimeoutRef = useRef(null)

  useEffect(() => {
    fetchCoffeeCastVideos()
  }, [])

  useEffect(
    () => () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current)
      }
    },
    []
  )

  const fetchCoffeeCastVideos = async () => {
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getCoffeeCastVideoList()
      const responseData = response?.data

      setSelectedVideoDetails(responseData[0])
      setCoffeeCastVideos(responseData)
    } finally {
      setLoading(false)
    }
  }

  // Exit Picture-in-Picture when returning to this screen
  useEffect(() => {
    const exitPictureInPicture = async () => {
      // Check if Picture-in-Picture API is supported
      if (!document.pictureInPictureEnabled) {
        return
      }

      // Check if any element is currently in Picture-in-Picture mode
      if (document.pictureInPictureElement) {
        try {
          // Get the video element from the player
          const player = playerRef.current

          if (player) {
            const videoElement = player.el().querySelector('video')

            // Check if the video element in PiP belongs to this player
            if (videoElement && document.pictureInPictureElement === videoElement) {
              // Pause the video before exiting PiP
              if (!player.paused()) {
                player.pause()
              }
            }
          }

          // Exit Picture-in-Picture
          await document.exitPictureInPicture()
        } catch (error) {
          // Silently handle errors (e.g., if PiP was already closed)
          // console.warn('Failed to exit Picture-in-Picture:', error)
        }
      }
    }

    // Exit PiP when component mounts or when location changes (user returns to this screen)
    exitPictureInPicture()
  }, [location.pathname])

  const videoJsOptions = React.useMemo(
    () => ({
      autoplay: false,
      muted: false,
      controls: true,
      responsive: true,
      fluid: true,
      controlBar: {
        skipButtons: { forward: 10, backward: 10 }
      },
      sources: [
        {
          src: selectedVideoDetails?.videoUrl,
          type: 'video/mp4'
        }
      ],
      poster: selectedVideoDetails?.thumbnail
    }),
    [selectedVideoDetails]
  )

  const showOverlay = (type, text) => {
    // Clear any existing timeout
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current)
    }

    // Show overlay
    setOverlay({ show: true, type, text })

    // Hide overlay after 700ms
    overlayTimeoutRef.current = setTimeout(() => {
      setOverlay({ show: false, type: null, text: '' })
    }, 700)
  }

  // Setup skip button handlers using event delegation
  const setupSkipButtonHandlers = (player) => {
    if (!player) return

    const playerEl = player.el()
    const controlBar = playerEl.querySelector('.vjs-control-bar')

    if (!controlBar) return

    // Use event delegation on the control bar to catch clicks on skip buttons
    // This works even if buttons are recreated
    const handleControlBarClick = (e) => {
      // Check if click is on skip button or its children
      const forwardButton = e.target.closest('.vjs-skip-forward, [class*="skip-forward"]')
      const backwardButton = e.target.closest('.vjs-skip-backward, [class*="skip-backward"]')

      if (forwardButton) {
        showOverlay('forward', '+10')
      } else if (backwardButton) {
        showOverlay('backward', '-10')
      }
    }

    // Remove existing listener if any (using a named function reference)
    if (controlBar._skipButtonHandler) {
      controlBar.removeEventListener('click', controlBar._skipButtonHandler)
    }

    // Add new listener and store reference
    controlBar._skipButtonHandler = handleControlBarClick
    controlBar.addEventListener('click', handleControlBarClick)
  }

  const handlePlayerReady = (player) => {
    playerRef.current = player

    player.on('play', () => {
      setIsPaused(false)
      showOverlay('play', '▶')
    })

    player.on('pause', () => {
      setIsPaused(true)
      showOverlay('pause', '⏸')
    })

    player.on('timeupdate', () => {
      const current = player.currentTime()
      const breaks = breakpointsRef.current

      const idx = getActiveBreakpointIndex(current, breaks)

      setActiveBreakpointIndex((prev) => (prev === idx ? prev : idx))
    })

    // Handle Picture-in-Picture exit - pause video when PiP is closed
    const videoElement = player.el().querySelector('video')

    if (videoElement) {
      videoElement.addEventListener('leavepictureinpicture', () => {
        if (!player.paused()) {
          player.pause()
        }
      })
    }

    // Attach click handlers to skip buttons
    // Setup skip buttons after a short delay to ensure they're rendered
    setTimeout(() => {
      setupSkipButtonHandlers(player)
    }, 200)

    // Also try to setup when control bar is ready
    player.ready(() => {
      setTimeout(() => {
        setupSkipButtonHandlers(player)
      }, 200)
    })
  }

  const safePlay = (player) => {
    if (!player || typeof player.play !== 'function') return
    const playResult = player.play && player.play().catch(() => {})

    if (playResult && typeof playResult.then === 'function') {
      playResult.catch((err) => {
        // Autoplay prevented or other play error — don't crash, optionally show UI
        // console.warn('Autoplay prevented or play failed:', err)
      })
    }
  }

  useEffect(() => {
    if (!playerRef.current || !selectedVideoDetails) return

    const player = playerRef.current

    player.src({
      src: selectedVideoDetails.videoUrl,
      type: 'video/mp4'
    })

    player.on('loadedmetadata', () => {
      renderChapterSegments(player, selectedVideoDetails.breakpoints)
    })

    breakpointsRef.current = selectedVideoDetails?.breakpoints || []
    if (breakpointsRef.current.length > 0) {
      setActiveBreakpointIndex(0)
    } else {
      setActiveBreakpointIndex(null)
    }
  }, [selectedVideoDetails])

  useEffect(() => {
    breakpointsRef.current = selectedVideoDetails?.breakpoints || []
    if (breakpointsRef.current.length > 0) {
      setActiveBreakpointIndex(0)
    } else {
      setActiveBreakpointIndex(null)
    }
  }, [selectedVideoDetails])

  const getActiveBreakpointIndex = (currentTime, breakpoints) => {
    if (!breakpoints || !breakpoints.length) return null

    let activeIndex = 0

    for (let i = 0; i < breakpoints.length; i++) {
      const bpTime = convertTimeToSeconds(breakpoints[i].time)

      if (currentTime >= bpTime) {
        activeIndex = i
      } else {
        break
      }
    }

    return activeIndex
  }

  useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement('video-js')

      videoElement.setAttribute('playsinline', '')
      videoElement.classList.add('vjs-big-play-centered')

      videoRef.current.appendChild(videoElement)

      const player = videojs(videoElement, videoJsOptions, () => {
        handlePlayerReady(player)
      })

      playerRef.current = player
    }
  }, [videoRef, videoJsOptions])

  useEffect(() => {
    const handleKeyDown = (e) => {
      const player = playerRef.current

      if (!player) return

      if (e.code === 'ArrowRight') {
        player.currentTime(player.currentTime() + 10)
        showOverlay('forward', '+10')
      } else if (e.code === 'ArrowLeft') {
        player.currentTime(player.currentTime() - 10)
        showOverlay('backward', '-10')
      } else if (e.code === 'Space') {
        e.preventDefault()
        if (player.paused()) {
          player.play().catch(() => {})
        } else {
          player.pause()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (playerRef.current && selectedVideoDetails) {
      playerRef.current.src({
        src: selectedVideoDetails.videoUrl,
        type: 'video/mp4'
      })

      playerRef.current.poster(selectedVideoDetails.thumbnail)

      playerRef.current.on('loadedmetadata', () => {
        renderChapterSegments(playerRef.current, selectedVideoDetails.breakpoints)
        // Setup skip buttons after metadata is loaded
        setTimeout(() => {
          setupSkipButtonHandlers(playerRef.current)
        }, 200)
      })
    }
  }, [selectedVideoDetails])

  const convertTimeToSeconds = (time) => {
    const parts = time.split(':').map(Number)

    if (parts.length === 2) return parts[0] * 60 + parts[1]

    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]

    return 0
  }

  const renderChapterSegments = (player, breakpoints) => {
    document.querySelectorAll('.chapter-segment').forEach((e) => e.remove())

    if (!breakpoints?.length) return

    const duration = player.duration()

    if (!duration) return

    const progressHolder = player.el().querySelector('.vjs-progress-holder')

    if (!progressHolder) return

    breakpoints?.forEach((bp, idx) => {
      const start = convertTimeToSeconds(bp.time)
      const end = breakpoints[idx + 1] ? convertTimeToSeconds(breakpoints[idx + 1].time) : duration

      const left = (start / duration) * 100
      const width = ((end - start) / duration) * 100

      const segment = document.createElement('div')

      segment.className = 'chapter-segment'
      segment.style.position = 'absolute'
      segment.style.left = `${left}%`
      segment.style.width = `calc(${width}% - 4px)`
      segment.style.height = '100%'
      segment.style.background = '#a0a0a0'
      segment.style.opacity = '1'

      segment.style.marginLeft = '2px'
      segment.style.marginRight = '2px'
      segment.style.opacity = '0.6'
      segment.style.cursor = 'pointer'

      segment.onclick = () => {
        player.currentTime(start)
        player.play().catch(() => {})

        safePlay(player)
      }

      progressHolder.appendChild(segment)
    })
  }

  const handleVideoClick = (item) => {
    setIsFullText(false)
    setSelectedVideoDetails(item)

    if (playerRef.current) {
      playerRef.current.src({
        src: item.videoUrl,
        type: 'video/mp4'
      })

      playerRef.current.poster(item.thumbnail)

      playerRef.current.on('loadedmetadata', () => {
        renderChapterSegments(playerRef.current, item.breakpoints)

        playerRef.current.play().catch(() => {
          // Autoplay blocked → user must interact
        })
      })

      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const handleBreakoutClick = (breakpoint, index) => {
    const secs = convertTimeToSeconds(breakpoint.time)

    if (playerRef.current) {
      playerRef.current.currentTime(secs)
      playerRef.current.play()
    }

    setActiveBreakpointIndex(index)
  }

  const toggleText = () => {
    setIsFullText(!isFullText)
  }

  return (
    <>
      {!userDetails || userDetails?.isCoffeeCastAvailable ? (
        coffeeCastVideos?.length > 0 && (
          <div className={`cast-section ${!userDetails ? 'guest-cast-section' : ''}`}>
            <div className="video-player-wrapper" ref={topRef}>
              <div className="post-wrapper">
                <div className="video-wrapper" data-vjs-player>
                  <div ref={videoRef}></div>
                  {overlay.show && <div className="skip-overlay">{overlay.text}</div>}
                </div>

                <div className="profile-section">
                  <div className="profile-avatar">
                    <img src={selectedVideoDetails?.profileLogo} alt="profile-image" />
                  </div>
                  <div className="profile-info">
                    <h4 className="profile-name">{selectedVideoDetails?.channel}</h4>
                    <p className="profile-description">
                      <p className="profile-description">
                        {selectedVideoDetails?.subtitle}
                        {selectedVideoDetails?.subtitle && selectedVideoDetails?.country && ' - '}
                        {selectedVideoDetails?.country}
                      </p>
                    </p>
                  </div>
                </div>

                <p className="post-caption">
                  {isFullText ? selectedVideoDetails?.postDescription : selectedVideoDetails?.postDescription?.slice(0, 119)}
                  {selectedVideoDetails?.postDescription?.length > 119 && !isFullText && '...'}
                  {selectedVideoDetails?.postDescription?.length > 119 && (
                    <button className="read-more-btn" onClick={toggleText}>
                      {isFullText ? t('READ_LESS') : t('READ_MORE')}
                    </button>
                  )}
                </p>
              </div>

              <div className="breakout-panel">
                <div className="breakout-header">
                  <h5 className="section-title">{t('BREAKOUT')}</h5>
                </div>

                <div className="breakout-content">
                  {selectedVideoDetails?.breakpoints?.map((breakpoint, index) => (
                    <>
                      <div key={index} className="breakout-item" onClick={() => handleBreakoutClick(breakpoint, index)}>
                        <div className="thumbnail-wrapper">
                          <img src={breakpoint.timeLineImage} alt="thumbnail" className="thumbnail-image" />
                        </div>
                        <div className="video-details">
                          <div className="video-summary">
                            <span className="video-title">{breakpoint.title}</span>
                          </div>
                          <span className="video-duration">{breakpoint.time}</span>
                        </div>
                        {activeBreakpointIndex === index && (
                          <div className={`equalizer-wrapper ${isPaused ? 'paused' : ''}`}>
                            <div className="bar" />
                            <div className="bar" />
                            <div className="bar" />
                            <div className="bar" />
                            <div className="bar" />
                          </div>
                        )}
                      </div>
                      <Divider />
                    </>
                  ))}
                </div>
              </div>
            </div>

            <div className="video-list">
              {coffeeCastVideos?.map((item, index) => (
                <div key={index} className="video-list-item" onClick={() => handleVideoClick(item)}>
                  <div className="video-card">
                    <div className="video-card-thumbnail">
                      <img src={item?.thumbnail} alt={`${item?.title} thumbnail`} />
                    </div>

                    <div className={'video-card-info'}>
                      <div className="video-card-profile-logo">
                        <img src={item?.profileLogo} alt={`${item?.channel} logo`} />
                      </div>
                      <div className={`video-card-title ${item?._id === '68ece52247115c636c9154f6' ? 'channel-centre' : ''}`}>{item?.title}</div>
                    </div>

                    <div className={`video-card-channel ${item?._id === '68ece52247115c636c9154f6' ? 'channel-centre' : ''}`}>{item?.subtitle}</div>
                    <div className="video-card-time">{item?.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <ComingSoonPopUp />
      )}
    </>
  )
}

export default CoffeeCast
